import {
  Box,
  Button,
  Icon,
  Image,
  Skeleton,
  SxProps,
  Typography,
} from "@outschool/backpack";
import { REFER_CREDIT_CENTS } from "@outschool/business-rules";
import {
  IconDefinition,
  faFacebook,
  faFacebookMessenger,
} from "@outschool/icons";
import {
  KakaoLocales,
  LineLocales,
  renderCurrencyString,
  useLocale,
  useTranslation,
} from "@outschool/localization";
import {
  facebookGeneralSendUrl,
  facebookGeneralShareUrl,
  outschoolEastAsiaReferralUrl,
} from "@outschool/routes";
import {
  TrackedButton,
  TrackedButtonProps,
} from "@outschool/ui-components-shared";
import { useScript } from "@outschool/ui-utils";
import React from "react";

import KakaoSVG from "../../images/kakaotalk.svg";
import LineSVG from "../../images/line.svg";
import KakaoImage from "../../images/kakao.webp";
import { KAKAO_JAVASCRIPT_KEY } from "../Env";
import { getExtraMetaData } from "./Referrals";
import { addShareUtmCampaign } from "./utils";

type ShareButtonsProps = {
  sx?: SxProps;
  [key: string]: any;
};

export default function DesktopReferralShareButton({
  trackingSharedProperties,
  shareLink,
  modalContext,
  sx = {},
  facebookAppId,
  usid,
}: ShareButtonsProps) {
  const locale = useLocale();
  const showLine = LineLocales.includes(locale);
  const showKakao = KakaoLocales.includes(locale);
  const showMeta = !showLine && !showKakao;

  const eastAsiaReferralUrl = outschoolEastAsiaReferralUrl(locale, usid) ?? "";

  return (
    <Box
      flex
      sx={[
        {
          flexDirection: "row",
          gap: "0.5em",
          justifyContent: "space-evenly",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {showKakao && (
        <KakaoShareButton
          trackingSharedProperties={trackingSharedProperties}
          url={eastAsiaReferralUrl}
        />
      )}
      {showLine && (
        <LineShareButton
          url={eastAsiaReferralUrl}
          trackingSharedProperties={trackingSharedProperties}
        />
      )}
      {showMeta && (
        <>
          <FacebookShareButton
            trackingSharedProperties={trackingSharedProperties}
            url={addShareUtmCampaign(shareLink, modalContext, "facebook")}
            facebookAppId={facebookAppId}
          />
          <MessengerShareButton
            trackingSharedProperties={trackingSharedProperties}
            url={addShareUtmCampaign(shareLink, modalContext, "messenger")}
            facebookAppId={facebookAppId}
          />
        </>
      )}
    </Box>
  );
}

const SOCIAL_COLOR = "rgba(204, 212, 255, 0.5)"; // #CCD4FF @ 50%
const SOCIAL_COLOR_HOVER = "#CCD4FF";
const SOCIAL_COLOR_ACTIVE = "rgba(97, 97, 242, 0.4)"; // #CCD4FF @ 40%

function CircularShareButton({
  label,
  icon,
  href,
  trackingSharedProperties,
  ...props
}: {
  label: string;
  icon: IconDefinition;
  href: string;
} & Omit<TrackedButtonProps<"a">, "component">) {
  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.875em",
      }}
    >
      <TrackedButton
        color="inherit"
        component="a"
        sx={{
          width: "56px",
          height: "56px",
          border: "none",

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          // round
          borderRadius: "50%",
          padding: 0,

          // colors
          color: "common.white",
          borderColor: SOCIAL_COLOR,
          backgroundColor: SOCIAL_COLOR,
          "&:hover:not(:disabled), &:focus:not(:disabled)": {
            color: "common.white",
            borderColor: SOCIAL_COLOR_HOVER,
            backgroundColor: SOCIAL_COLOR_HOVER,
            textDecoration: "none",
          },
          "&:active:not(:disabled)": {
            color: "common.white",
            borderColor: SOCIAL_COLOR_ACTIVE,
            backgroundColor: SOCIAL_COLOR_ACTIVE,
          },
        }}
        size="large"
        trackingSharedProperties={{
          ...trackingSharedProperties,
          ...getExtraMetaData(href),
        }}
        href={href}
        {...props}
      >
        <Icon icon={icon} sx={{ color: "primary.600" }} />
      </TrackedButton>
      {label}
    </Box>
  );
}

type DesktopReferralShareButtonProps = {
  url: string;
  text?: string;
  facebookAppId?: string;
} & TrackedButtonProps<"a">;

function FacebookShareButton({
  url,
  facebookAppId,
  ...props
}: DesktopReferralShareButtonProps) {
  const { t } = useTranslation(
    "ui-components-website\\Sharing\\DesktopReferralShareButton"
  );
  return (
    <CircularShareButton
      {...props}
      target="_blank"
      trackingName="share_via_facebook"
      label={t("Facebook")}
      href={facebookGeneralShareUrl(url, facebookAppId)}
      icon={faFacebook}
    />
  );
}

function MessengerShareButton({
  url,
  facebookAppId,
  ...props
}: DesktopReferralShareButtonProps) {
  const { t } = useTranslation(
    "ui-components-website\\Sharing\\DesktopReferralShareButton"
  );
  return (
    <CircularShareButton
      {...props}
      target="_blank"
      trackingName="share_via_messenger"
      label={t("Messenger")}
      href={facebookGeneralSendUrl(url, facebookAppId)}
      icon={faFacebookMessenger}
    />
  );
}

// https://developers.kakao.com/docs/latest/en/message/message-template#feed
type KakaoFeedMessageTemplate = {
  objectType: string;
  buttonTitle: string;
  content: {
    title: string;
    description: string;
    imageUrl: string;
    link: {
      mobileWebUrl: string;
      webUrl: string;
    };
  };
};

declare global {
  interface Window {
    Kakao: {
      init: (kakaoJavaScriptKey: string) => void;
      isInitialized: () => boolean;
      Share: {
        sendDefault: (settings: KakaoFeedMessageTemplate) => void;
      };
    };
  }
}

function KakaoShareButton({
  url,
  trackingSharedProperties,
}: DesktopReferralShareButtonProps) {
  const kakaoJavaScriptKey = KAKAO_JAVASCRIPT_KEY;

  const { t } = useTranslation(
    "ui-components-website\\Sharing\\DesktopReferralShareButton"
  );
  const kakaoSdkUrl = "https://t1.kakaocdn.net/kakao_js_sdk/2.7.1/kakao.min.js";

  const Kakao: Window["Kakao"] | undefined = useScript({
    src: kakaoSdkUrl,
    variableName: "Kakao",
  });
  if (Kakao !== undefined && !Kakao.isInitialized() && kakaoJavaScriptKey) {
    Kakao.init(kakaoJavaScriptKey);
  }

  const feedSettings: KakaoFeedMessageTemplate = {
    objectType: "feed",
    buttonTitle: t("Sign up and receive benefits"),
    content: {
      title: t("Outschool Friend Invitation Points"),
      description: t("Earn {{creditString}} credit when you sign up!", {
        creditString: renderCurrencyString({
          priceInCents: REFER_CREDIT_CENTS,
          showCurrencyCode: false,
        }),
      }),
      imageUrl: KakaoImage,
      link: {
        mobileWebUrl: url,
        webUrl: url,
      },
    },
  };

  const KAKAO_YELLOW = "#FEE500";
  const BUTTON_HEIGHT = 48;

  return (
    <>
      {Kakao !== undefined && Kakao.isInitialized() ? (
        <TrackedButton
          component="button"
          trackingName="share_via_kakao"
          onClick={() => Kakao.Share.sendDefault(feedSettings)}
          sx={{
            target: "_blank",
            width: "100%",
            height: BUTTON_HEIGHT,
            border: "none",
            "&:hover:not(:disabled)": {
              border: "none",
            },
            "&:active:not(:disabled)": {
              border: "none",
            },
            margin: "0px",
            padding: "0px",
          }}
          trackingSharedProperties={{
            ...trackingSharedProperties,
            ...getExtraMetaData(kakaoSdkUrl),
          }}
        >
          <Button
            component="span"
            color="inherit"
            sx={{
              width: "100%",
              height: "100%",
              color: "common.black",
              backgroundColor: KAKAO_YELLOW,
              border: "none",
              "&:hover:not(:disabled)": {
                backgroundColor: KAKAO_YELLOW,
                border: "none",
                filter: "brightness(0.90)",
              },
              "&:active:not(:disabled)": {
                backgroundColor: KAKAO_YELLOW,
                border: "none",
                filter: "brightness(0.80)",
              },
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "1.75rem",
                flex: 1,
                textAlign: "center",
              }}
            >
              {t("Share On Kakao")}
            </Typography>
            <Image
              src={KakaoSVG}
              alt={t("Kakao Talk Icon")}
              sx={{
                position: "absolute",
                right: 0,
                marginRight: BUTTON_HEIGHT / 2,
              }}
            />
          </Button>
        </TrackedButton>
      ) : (
        <Skeleton
          height={BUTTON_HEIGHT}
          variant="rounded"
          width={"100%"}
          sx={{ borderRadius: BUTTON_HEIGHT / 2 }}
        />
      )}
    </>
  );
}

function LineShareButton({
  url,
  text,
  trackingSharedProperties,
}: DesktopReferralShareButtonProps) {
  const { t } = useTranslation(
    "ui-components-website\\Sharing\\DesktopReferralShareButton"
  );
  const baseUrl = "https://social-plugins.line.me/lineit/share";
  const lineShareUrl = new URL(baseUrl);

  if (typeof url === "string" && url.trim().length > 0) {
    lineShareUrl.searchParams.set("url", url);
  }

  if (typeof text === "string" && text.trim().length > 0) {
    lineShareUrl.searchParams.set("text", text);
  }

  const href = lineShareUrl.toString();

  const LINE_GREEN = "#07B53B";
  const BUTTON_HEIGHT = 48;

  return (
    <TrackedButton
      component="a"
      href={href}
      target="_blank"
      trackingName="share_via_line"
      color="inherit"
      sx={{
        width: "100%",
        height: "100%",
        border: "none",
        "&:hover:not(:disabled)": {
          border: "none",
        },
        "&:active:not(:disabled)": {
          border: "none",
        },
        margin: "0px",
        padding: "0px",
      }}
      trackingSharedProperties={{
        ...trackingSharedProperties,
        ...getExtraMetaData(href),
      }}
    >
      <Button
        component="span"
        color="inherit"
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          color: "common.white",
          backgroundColor: LINE_GREEN,
          border: "none",
          "&:hover:not(:disabled)": {
            backgroundColor: LINE_GREEN,
            border: "none",
            filter: "brightness(0.90)",
          },
          "&:active:not(:disabled)": {
            backgroundColor: LINE_GREEN,
            border: "none",
            filter: "brightness(0.80)",
          },
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: "1.75rem",
            flex: 1,
            textAlign: "center",
          }}
        >
          {t("Share on Line")}
        </Typography>
        <Image
          src={LineSVG}
          alt={t("Line Icon")}
          sx={{
            position: "absolute",
            right: 0,
            marginRight: BUTTON_HEIGHT / 2,
          }}
        />
      </Button>
    </TrackedButton>
  );
}
